import { useCallback, useEffect, useRef, useState } from 'react';
import {
  errorToast,
  errorToastPromise,
  successToast,
} from '../../../../utils/toast';
import { Appointment } from '../../../../types/appointments';
import {
  UserData,
  UserFormValues,
  UsersFilters,
  UsersResponseData,
} from '../../../../types/users';
import { getAllUsers } from '../../../../services/users';
import {
  EXISTING_APPOINTMENTS_QUERY_KEY,
  useMutateBookAppointment,
  useMutateLockAppointment,
  useMutateQueueAppointment,
  useMutateUnLockAppointment,
} from '../../../../query/appointments';
import { BookAppointmentForm } from '../../../../components/views-components/staff/appointments/EditAppointmentData';
import { useCommonTranslation } from '../../../../hooks/i18n/useCommonTranslation';
import { useQueryClient } from 'react-query';
import { getReferrerIds } from '../../../../components/views-components/staff/appointments/AppointmentReferrersUtil';
import { UNSAFE_FILTER_WITH_NO_PAGINATION } from '../../../../hooks/usePaginatedFilters';
import { checkErrorCode } from '../../../../utils/errors';
import { ErrorCode } from '../../../../utils/errorCodes';

export const useLockUnlockSelectedAppointment = ({
  selectedAppointment,
  onError,
}: {
  selectedAppointment?: Appointment;
  onError: () => void;
}) => {
  const currentLockedOrLockingApptIdRef = useRef<string | undefined>(
    selectedAppointment?.id,
  );

  const doNotUnlockOnUnmount = useCallback(() => {
    currentLockedOrLockingApptIdRef.current = undefined;
  }, [currentLockedOrLockingApptIdRef]);

  const { mutate: lockAppointment, isLoading } = useMutateLockAppointment({
    onError: (error) => {
      errorToast(error);
      // We were not able to lock, no point unlocking. This was causing BE errors that should now be fixed.
      doNotUnlockOnUnmount();
      onError();
    },
  });

  const { mutate: unLockAppointment } = useMutateUnLockAppointment({
    onError: () => {
      /**
       * Ignore errors, the unlocking may have been triggered because BE
       * have expired the appointment. Any locked appointment the BE
       * will eventually expire in any case, unlocking here is implemented
       * for faster free appointment recovery.
       */
    },
  });

  /**
   * The appointment object can change because of the locking and background refresh
   * on the underlying appointment list. So we need to rely on the id to see if
   * a new appointment should be locked or unlocked.
   */
  const selectedAppointmentId = selectedAppointment?.id;
  useEffect(() => {
    if (!selectedAppointmentId && currentLockedOrLockingApptIdRef.current) {
      unLockAppointment(currentLockedOrLockingApptIdRef.current);
    }

    if (
      selectedAppointmentId &&
      currentLockedOrLockingApptIdRef.current !== selectedAppointmentId
    ) {
      currentLockedOrLockingApptIdRef.current = selectedAppointmentId;
      lockAppointment(selectedAppointmentId);
      return () => {
        /**
         * This will unlock the appointment if the appointment dialog was
         * abandoned, or another appointment was selected.
         */
        if (currentLockedOrLockingApptIdRef.current) {
          unLockAppointment(currentLockedOrLockingApptIdRef.current);
          currentLockedOrLockingApptIdRef.current = undefined;
        }
      };
    }
  }, [
    lockAppointment,
    unLockAppointment,
    currentLockedOrLockingApptIdRef,
    selectedAppointmentId,
  ]);

  return { doNotUnlockOnUnmount, isLoading };
};

export type DuplicateUserResult = UsersResponseData | undefined;

const getDuplicateUserQuery = async (
  filters: UsersFilters,
): Promise<DuplicateUserResult> => {
  const duplicateUsers = await errorToastPromise(getAllUsers(filters));
  if (duplicateUsers?.data?.data?.length) {
    return duplicateUsers.data;
  }
};

export const getDuplicateUsers = async (
  user: UserFormValues,
): Promise<DuplicateUserResult> => {
  const duplicates = await getDuplicateUserQuery({
    firstname: user.firstname,
    lastname: user.lastname,
    birthDate: user.birthdate,
    ...UNSAFE_FILTER_WITH_NO_PAGINATION,
  });
  if (duplicates) {
    return duplicates;
  }
};

type UseSaveAndBookAppointment = {
  selectedAppointment: Appointment;
  setIsSaving: (val: boolean) => void;
};

export const useSaveAndBookAppointment = ({
  selectedAppointment,
  setIsSaving,
}: UseSaveAndBookAppointment) => {
  const { t } = useCommonTranslation();
  const { mutateAsync: bookAppointment } = useMutateBookAppointment({
    onSuccess: () => {
      successToast(t('appointment-booked-success'));
    },
    onError: (err) => {
      if (checkErrorCode(err, ErrorCode.DB_APPT_NOT_FOUND)) {
        errorToast(t('error-messages.staff_booking_DB0235'));
      } else {
        errorToast(err);
      }
    },
  });
  const { mutateAsync: queueAppointment } = useMutateQueueAppointment({
    onSuccess: () => {
      successToast(t('appointment-queued-success'));
    },
  });
  const client = useQueryClient();

  const saveAndBookAppointment = async ({
    user,
    appointment,
    shouldQueueAppointment,
  }: {
    user: UserData;
    appointment: BookAppointmentForm;
    shouldQueueAppointment: boolean;
  }) => {
    setIsSaving(true);
    try {
      await bookAppointment({
        id: selectedAppointment!.id,
        client_id: user.id,
        ...appointment,
        ...getReferrerIds(appointment, user),
      });
      if (shouldQueueAppointment) {
        await queueAppointment(selectedAppointment.id);
      }
    } finally {
      // noinspection ES6MissingAwait
      /**
       * Current appointment did not previously exist in existing appts,
       * so it would not be updated by cache updating and needs to be invalidated.
       */
      client.invalidateQueries(EXISTING_APPOINTMENTS_QUERY_KEY);
      setIsSaving(false);
    }
  };
  return { saveAndBookAppointment };
};

export type BookPatientApptsProps = {
  appointment: BookAppointmentForm;
  keepPatient?: boolean;
  queuePatient?: boolean;
  user: UserData;
};

export const useStaffAppointmentBooking = ({
  closeForm,
  selectedAppointment,
}: {
  closeForm: (keepPatient: boolean) => void;
  selectedAppointment?: Appointment;
}) => {
  const closeFormKeepPatient = useCallback(() => {
    closeForm(true);
  }, [closeForm]);

  const { isLoading: isLockAppointmentLoading, doNotUnlockOnUnmount } =
    useLockUnlockSelectedAppointment({
      onError: closeFormKeepPatient,
      selectedAppointment,
    });

  const [isSaving, setIsSaving] = useState(false);

  const { saveAndBookAppointment } = useSaveAndBookAppointment({
    selectedAppointment: selectedAppointment!,
    setIsSaving,
  });

  const bookPatientAppointment = async ({
    appointment,
    keepPatient = false,
    queuePatient = false,
    user,
  }: BookPatientApptsProps) => {
    setIsSaving(true);
    try {
      doNotUnlockOnUnmount();
      await saveAndBookAppointment({
        appointment,
        user: user,
        shouldQueueAppointment: queuePatient,
      });
      closeForm(keepPatient);
    } catch (exception) {
      setIsSaving(false);
    }
  };
  return {
    selectedAppointment,
    isLoading: isSaving || isLockAppointmentLoading,
    bookPatientAppointment,
  };
};
